import React, { useState } from 'react';
import { motion } from 'framer-motion';

// ============================================
// Mock Data - Completely Static, No Database
// ============================================
const mockStats = {
  totalProjects: 8,
  availableUnits: 42,
  soldUnits: 156,
  totalUnits: 198,
  pendingLeads: 23,
  totalRevenue: 48500000,
  totalCommissions: 2425000,
  thisMonthSales: 12,
  avgSalePrice: 310897,
};

const mockProjects = [
  { id: '1', name: 'Canggu Villas Collection', units: 24, available: 8, status: 'Active', revenue: 12500000 },
  { id: '2', name: 'Seminyak Luxury Residences', units: 36, available: 12, status: 'Active', revenue: 18200000 },
  { id: '3', name: 'Ubud Retreat Homes', units: 18, available: 6, status: 'Active', revenue: 8400000 },
  { id: '4', name: 'Uluwatu Ocean View', units: 12, available: 4, status: 'Pre-Launch', revenue: 0 },
];

const mockUnits = [
  { id: '1', name: 'Villa A-101', project: 'Canggu Villas', type: '3BR Villa', price: 425000, status: 'Available' },
  { id: '2', name: 'Villa A-102', project: 'Canggu Villas', type: '3BR Villa', price: 445000, status: 'Reserved' },
  { id: '3', name: 'Unit B-201', project: 'Seminyak Luxury', type: '2BR Apt', price: 285000, status: 'Sold' },
  { id: '4', name: 'Villa C-301', project: 'Ubud Retreat', type: '4BR Villa', price: 580000, status: 'Available' },
  { id: '5', name: 'Unit B-202', project: 'Seminyak Luxury', type: '2BR Apt', price: 295000, status: 'Available' },
];

const mockLeads = [
  { id: '1', name: 'Robert Chen', email: 'robert@example.com', stage: 'New', source: 'Website', interest: 'Canggu Villas' },
  { id: '2', name: 'Emma Thompson', email: 'emma@example.com', stage: 'Contacted', source: 'Referral', interest: 'Seminyak Luxury' },
  { id: '3', name: 'James Wilson', email: 'james@example.com', stage: 'Qualified', source: 'Portal', interest: 'Ubud Retreat' },
  { id: '4', name: 'Sofia Garcia', email: 'sofia@example.com', stage: 'Negotiating', source: 'Agent', interest: 'Uluwatu Ocean' },
];

const mockTeam = [
  { id: '1', name: 'Sarah Anderson', role: 'Sales Manager', sales: 28, commission: 142000, status: 'Active' },
  { id: '2', name: 'David Lee', role: 'Agent', sales: 15, commission: 75000, status: 'Active' },
  { id: '3', name: 'Maria Santos', role: 'Agent', sales: 22, commission: 110000, status: 'Active' },
  { id: '4', name: 'Alex Kim', role: 'Junior Agent', sales: 8, commission: 40000, status: 'Active' },
];

const mockCommissions = [
  { id: '1', agent: 'Sarah Anderson', unit: 'Villa A-103', salePrice: 425000, rate: 5, amount: 21250, status: 'Paid', date: '2025-01-15' },
  { id: '2', agent: 'David Lee', unit: 'Unit B-205', salePrice: 295000, rate: 5, amount: 14750, status: 'Pending', date: '2025-01-18' },
  { id: '3', agent: 'Maria Santos', unit: 'Villa C-302', salePrice: 580000, rate: 5, amount: 29000, status: 'Processing', date: '2025-01-20' },
  { id: '4', agent: 'Sarah Anderson', unit: 'Villa A-104', salePrice: 445000, rate: 5, amount: 22250, status: 'Paid', date: '2025-01-12' },
];

const mockInvestors = [
  { id: '1', name: 'Pacific Capital Group', type: 'Institutional', invested: 8500000, units: 12, returns: 680000, status: 'Active' },
  { id: '2', name: 'Chen Family Office', type: 'Family Office', invested: 3200000, units: 5, returns: 256000, status: 'Active' },
  { id: '3', name: 'Horizon Ventures', type: 'VC', invested: 5000000, units: 8, returns: 400000, status: 'Active' },
  { id: '4', name: 'Individual - James Miller', type: 'Individual', invested: 850000, units: 2, returns: 68000, status: 'Active' },
];

const mockMedia = [
  { id: '1', name: 'Canggu Villas Brochure.pdf', type: 'PDF', size: '4.2 MB', project: 'Canggu Villas', date: '2025-01-10' },
  { id: '2', name: 'Villa A-101 Photos.zip', type: 'Images', size: '28 MB', project: 'Canggu Villas', date: '2025-01-08' },
  { id: '3', name: 'Seminyak Drone Video.mp4', type: 'Video', size: '156 MB', project: 'Seminyak Luxury', date: '2025-01-05' },
  { id: '4', name: 'Price List Q1 2025.xlsx', type: 'Spreadsheet', size: '1.1 MB', project: 'All Projects', date: '2025-01-01' },
  { id: '5', name: 'Floor Plans - Ubud.pdf', type: 'PDF', size: '8.5 MB', project: 'Ubud Retreat', date: '2024-12-28' },
];

const mockSalesTools = [
  { id: '1', name: 'Price Calculator', description: 'Calculate unit prices with customizations', icon: 'calculator', usage: 156 },
  { id: '2', name: 'Proposal Generator', description: 'Create professional proposals', icon: 'document', usage: 89 },
  { id: '3', name: 'ROI Simulator', description: 'Project investment returns', icon: 'chart', usage: 234 },
  { id: '4', name: 'Availability Matrix', description: 'Real-time unit availability', icon: 'grid', usage: 312 },
  { id: '5', name: 'Commission Calculator', description: 'Calculate agent commissions', icon: 'percent', usage: 178 },
];

const mockFinancials = {
  revenue: { current: 48500000, previous: 42100000, change: 15.2 },
  expenses: { current: 12400000, previous: 11800000, change: 5.1 },
  profit: { current: 36100000, previous: 30300000, change: 19.1 },
  pendingPayments: 2850000,
  monthlyData: [
    { month: 'Aug', revenue: 3200000, expenses: 890000 },
    { month: 'Sep', revenue: 4100000, expenses: 1020000 },
    { month: 'Oct', revenue: 3800000, expenses: 980000 },
    { month: 'Nov', revenue: 4500000, expenses: 1100000 },
    { month: 'Dec', revenue: 5200000, expenses: 1250000 },
    { month: 'Jan', revenue: 4800000, expenses: 1050000 },
  ],
};

type PageType = 'overview' | 'projects' | 'units' | 'team' | 'leads' | 'financial' | 'commissions' | 'investors' | 'tools' | 'media' | 'settings';

interface NavItem {
  key: PageType;
  label: string;
  icon: React.ReactNode;
}

// Format USD currency
const formatUSD = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// ============================================
// Lucide-style Icon Components (matching actual app)
// ============================================
const BarChart3Icon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path d="M3 3v18h18" /><path d="M18 17V9" /><path d="M13 17V5" /><path d="M8 17v-3" />
  </svg>
);

const BuildingIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <rect x="4" y="2" width="16" height="20" rx="2" ry="2" /><path d="M9 22v-4h6v4" /><path d="M8 6h.01" /><path d="M16 6h.01" /><path d="M12 6h.01" /><path d="M12 10h.01" /><path d="M12 14h.01" /><path d="M16 10h.01" /><path d="M16 14h.01" /><path d="M8 10h.01" /><path d="M8 14h.01" />
  </svg>
);

const HomeIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9,22 9,12 15,12 15,22" />
  </svg>
);

const UsersIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const MessageSquareIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const DollarSignIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

const PieChartIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path d="M21.21 15.89A10 10 0 1 1 8 2.83" /><path d="M22 12A10 10 0 0 0 12 2v10z" />
  </svg>
);

const TrendingUpIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <polyline points="23,6 13.5,15.5 8.5,10.5 1,18" /><polyline points="17,6 23,6 23,12" />
  </svg>
);

const WrenchIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
  </svg>
);

const FileImageIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" /><polyline points="14,2 14,8 20,8" /><circle cx="10" cy="13" r="2" /><path d="m20 17-1.09-1.09a2 2 0 0 0-2.82 0L10 22" />
  </svg>
);

const SettingsIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const PanelLeftIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <rect width="18" height="18" x="3" y="3" rx="2" /><path d="M9 3v18" />
  </svg>
);

const SearchIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
  </svg>
);

const BellIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
  </svg>
);

const ArrowUpRightIcon = () => (
  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path d="M7 17 17 7" /><path d="M7 7h10v10" />
  </svg>
);

const ArrowDownRightIcon = () => (
  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path d="M7 7 17 17" /><path d="M17 7v10H7" />
  </svg>
);

const CheckIcon = () => (
  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <polyline points="20,6 9,17 4,12" />
  </svg>
);

const DownloadIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7,10 12,15 17,10" /><line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const UploadIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17,8 12,3 7,8" /><line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const FileIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" /><polyline points="14,2 14,8 20,8" />
  </svg>
);

const ImageIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21,15 16,10 5,21" />
  </svg>
);

const VideoIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <polygon points="23,7 16,12 23,17 23,7" /><rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
  </svg>
);

const CalculatorIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <rect x="4" y="2" width="16" height="20" rx="2" /><line x1="8" y1="6" x2="16" y2="6" /><line x1="8" y1="10" x2="8" y2="10.01" /><line x1="12" y1="10" x2="12" y2="10.01" /><line x1="16" y1="10" x2="16" y2="10.01" /><line x1="8" y1="14" x2="8" y2="14.01" /><line x1="12" y1="14" x2="12" y2="14.01" /><line x1="16" y1="14" x2="16" y2="14.01" /><line x1="8" y1="18" x2="8" y2="18.01" /><line x1="12" y1="18" x2="16" y2="18" />
  </svg>
);

const GridIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
  </svg>
);

const PercentIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <line x1="19" y1="5" x2="5" y2="19" /><circle cx="6.5" cy="6.5" r="2.5" /><circle cx="17.5" cy="17.5" r="2.5" />
  </svg>
);

// Navigation items matching the actual PropertyBase admin
const navigationItems: NavItem[] = [
  { key: 'overview', label: 'Overview', icon: <BarChart3Icon /> },
  { key: 'projects', label: 'Projects', icon: <BuildingIcon /> },
  { key: 'units', label: 'Units', icon: <HomeIcon /> },
  { key: 'team', label: 'Team Management', icon: <UsersIcon /> },
  { key: 'leads', label: 'Leads', icon: <MessageSquareIcon /> },
  { key: 'financial', label: 'Financial', icon: <DollarSignIcon /> },
  { key: 'commissions', label: 'Commissions', icon: <PieChartIcon /> },
  { key: 'investors', label: 'Investors', icon: <TrendingUpIcon /> },
  { key: 'tools', label: 'Sales Tools', icon: <WrenchIcon /> },
  { key: 'media', label: 'Media', icon: <FileImageIcon /> },
  { key: 'settings', label: 'Settings', icon: <SettingsIcon /> },
];

// ============================================
// shadcn/ui-style Sidebar Component
// ============================================
interface DemoSidebarProps {
  activePage: PageType;
  onPageChange: (page: PageType) => void;
  collapsed: boolean;
}

const DemoSidebar: React.FC<DemoSidebarProps> = ({ activePage, onPageChange, collapsed }) => {
  return (
    <div
      className={`flex flex-col h-full bg-[#fafafa] dark:bg-zinc-950 border-r border-zinc-200 dark:border-zinc-800 transition-all duration-200 ${
        collapsed ? 'w-12' : 'w-56'
      }`}
    >
      {/* Logo Header */}
      <div className={`flex items-center border-b border-zinc-200 dark:border-zinc-800 ${collapsed ? 'p-2 justify-center' : 'p-4'}`}>
        <button
          onClick={() => onPageChange('overview')}
          className="flex items-center gap-2.5 hover:opacity-80 transition-opacity"
        >
          <div className="w-8 h-8 bg-gradient-to-br from-sky-500 to-sky-600 rounded-lg flex items-center justify-center flex-shrink-0">
            <span className="text-white text-sm font-bold">P</span>
          </div>
          {!collapsed && (
            <div className="text-left">
              <h1 className="text-sm font-semibold text-zinc-900 dark:text-white leading-none">PropertyBase</h1>
            </div>
          )}
        </button>
      </div>

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto py-2">
        <div className={`px-3 py-2 ${collapsed ? 'hidden' : ''}`}>
          <span className="text-[11px] font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Navigation</span>
        </div>
        <nav className="px-2 space-y-0.5">
          {navigationItems.map((item) => {
            const isActive = activePage === item.key;
            return (
              <button
                key={item.key}
                onClick={() => onPageChange(item.key)}
                className={`flex items-center gap-2 w-full rounded-md transition-colors ${
                  collapsed ? 'justify-center p-2' : 'px-2 py-1.5'
                } ${
                  isActive
                    ? 'bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-white'
                    : 'text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 hover:text-zinc-900 dark:hover:text-white'
                }`}
                title={collapsed ? item.label : undefined}
              >
                <span className={`flex-shrink-0 ${isActive ? 'text-zinc-900 dark:text-white' : 'text-zinc-500 dark:text-zinc-400'}`}>
                  {item.icon}
                </span>
                {!collapsed && (
                  <span className="text-[13px] font-medium truncate">{item.label}</span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* User Footer */}
      {!collapsed && (
        <div className="p-3 border-t border-zinc-200 dark:border-zinc-800">
          <div className="flex items-center gap-2.5 p-2 rounded-md bg-zinc-100 dark:bg-zinc-800/50">
            <div className="w-7 h-7 bg-gradient-to-br from-violet-500 to-violet-600 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-white font-medium text-[10px]">AD</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[12px] font-medium text-zinc-900 dark:text-white truncate">Admin User</p>
              <p className="text-[10px] text-zinc-500 truncate">admin@propertybase.ai</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// Page Content Components
// ============================================

// Overview Content (matching actual AdminOverviewTab style)
const OverviewContent: React.FC = () => (
  <div className="p-4 md:p-6 space-y-4">
    {/* Stats Grid - 4 columns like actual app */}
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {[
        { label: 'Total Projects', value: mockStats.totalProjects, change: '+2', icon: <BuildingIcon />, color: 'text-sky-600 dark:text-sky-400' },
        { label: 'Total Units', value: mockStats.totalUnits, change: `${mockStats.availableUnits} avail`, icon: <HomeIcon />, color: 'text-emerald-600 dark:text-emerald-400' },
        { label: 'Active Leads', value: mockStats.pendingLeads, change: '+5', icon: <MessageSquareIcon />, color: 'text-violet-600 dark:text-violet-400' },
        { label: 'Total Revenue', value: formatUSD(mockStats.totalRevenue), change: '+18%', icon: <DollarSignIcon />, color: 'text-amber-600 dark:text-amber-400' },
      ].map((stat, i) => (
        <div key={i} className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-3 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <div className={`w-7 h-7 rounded-lg bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center ${stat.color}`}>
              {stat.icon}
            </div>
            <div className="flex items-center gap-0.5 text-[10px] font-medium text-emerald-600 dark:text-emerald-400">
              <ArrowUpRightIcon />
              <span>{stat.change}</span>
            </div>
          </div>
          <div className="text-lg font-semibold text-zinc-900 dark:text-white">{stat.value}</div>
          <div className="text-[10px] text-zinc-500 dark:text-zinc-400 mt-0.5">{stat.label}</div>
        </div>
      ))}
    </div>

    {/* Charts Row */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-4 shadow-sm">
        <h3 className="text-sm font-semibold text-zinc-900 dark:text-white mb-3">Sales Performance</h3>
        <div className="h-28 flex items-end gap-1">
          {[35, 52, 48, 61, 45, 58, 72, 65, 78, 82, 75, 89].map((val, i) => (
            <div key={i} className="flex-1 flex flex-col justify-end">
              <div
                className="w-full bg-sky-500/20 dark:bg-sky-500/30 rounded-t transition-all hover:bg-sky-500/30"
                style={{ height: `${val}%` }}
              >
                <div className="w-full h-2/5 bg-sky-500 rounded-t" />
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-between mt-2 text-[10px] text-zinc-400">
          <span>Jan</span><span>Mar</span><span>Jun</span><span>Sep</span><span>Dec</span>
        </div>
      </div>
      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-4 shadow-sm">
        <h3 className="text-sm font-semibold text-zinc-900 dark:text-white mb-3">Unit Status</h3>
        <div className="flex items-center gap-6">
          <div className="relative w-24 h-24">
            <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
              <circle cx="18" cy="18" r="14" fill="none" className="stroke-zinc-200 dark:stroke-zinc-700" strokeWidth="3" />
              <circle cx="18" cy="18" r="14" fill="none" stroke="#0ea5e9" strokeWidth="3" strokeDasharray="62 26" strokeLinecap="round" />
              <circle cx="18" cy="18" r="14" fill="none" stroke="#10b981" strokeWidth="3" strokeDasharray="18 70" strokeDashoffset="-62" strokeLinecap="round" />
              <circle cx="18" cy="18" r="14" fill="none" stroke="#f59e0b" strokeWidth="3" strokeDasharray="8 80" strokeDashoffset="-80" strokeLinecap="round" />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-lg font-bold text-zinc-900 dark:text-white">{mockStats.totalUnits}</span>
            </div>
          </div>
          <div className="space-y-2 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full bg-sky-500" />
              <span className="text-zinc-600 dark:text-zinc-400">Sold ({mockStats.soldUnits})</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
              <span className="text-zinc-600 dark:text-zinc-400">Available ({mockStats.availableUnits})</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />
              <span className="text-zinc-600 dark:text-zinc-400">Reserved (12)</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Recent Activity */}
    <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-4 shadow-sm">
      <h3 className="text-sm font-semibold text-zinc-900 dark:text-white mb-3">Recent Activity</h3>
      <div className="space-y-3">
        {[
          { action: 'New lead added', detail: 'Robert Chen interested in Canggu Villas', time: '2m ago', type: 'lead' },
          { action: 'Unit sold', detail: 'Villa A-103 sold for $425,000', time: '1h ago', type: 'sale' },
          { action: 'Commission paid', detail: 'Sarah Anderson received $21,250', time: '3h ago', type: 'commission' },
        ].map((item, i) => (
          <div key={i} className="flex items-start gap-3 pb-3 border-b border-zinc-100 dark:border-zinc-800 last:border-0 last:pb-0">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
              item.type === 'lead' ? 'bg-violet-100 dark:bg-violet-500/20 text-violet-600 dark:text-violet-400' :
              item.type === 'sale' ? 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400' :
              'bg-amber-100 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400'
            }`}>
              <CheckIcon />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[12px] font-medium text-zinc-900 dark:text-white">{item.action}</p>
              <p className="text-[11px] text-zinc-500 truncate">{item.detail}</p>
            </div>
            <span className="text-[10px] text-zinc-400 flex-shrink-0">{item.time}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Projects Content (matching actual ProjectsTable style)
const ProjectsContent: React.FC = () => (
  <div className="p-4 md:p-6 space-y-4">
    <div className="flex items-center justify-between">
      <h2 className="text-base font-semibold text-zinc-900 dark:text-white">Projects</h2>
      <button className="px-3 py-1.5 bg-sky-500 hover:bg-sky-600 text-white text-xs font-medium rounded-md transition-colors">
        + Add Project
      </button>
    </div>
    <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg overflow-hidden shadow-sm">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-zinc-50 dark:bg-zinc-800/50 border-b border-zinc-200 dark:border-zinc-700">
            <th className="text-left py-2.5 px-4 font-medium text-zinc-500 dark:text-zinc-400">Project Name</th>
            <th className="text-left py-2.5 px-4 font-medium text-zinc-500 dark:text-zinc-400">Units</th>
            <th className="text-left py-2.5 px-4 font-medium text-zinc-500 dark:text-zinc-400">Available</th>
            <th className="text-left py-2.5 px-4 font-medium text-zinc-500 dark:text-zinc-400">Status</th>
            <th className="text-right py-2.5 px-4 font-medium text-zinc-500 dark:text-zinc-400">Revenue</th>
          </tr>
        </thead>
        <tbody>
          {mockProjects.map((project) => (
            <tr key={project.id} className="border-b border-zinc-100 dark:border-zinc-800 last:border-0 hover:bg-zinc-50 dark:hover:bg-zinc-800/30">
              <td className="py-2.5 px-4 font-medium text-zinc-900 dark:text-white">{project.name}</td>
              <td className="py-2.5 px-4 text-zinc-600 dark:text-zinc-400">{project.units}</td>
              <td className="py-2.5 px-4 text-zinc-600 dark:text-zinc-400">{project.available}</td>
              <td className="py-2.5 px-4">
                <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${
                  project.status === 'Active'
                    ? 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400'
                    : 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400'
                }`}>{project.status}</span>
              </td>
              <td className="py-2.5 px-4 text-right font-medium text-emerald-600 dark:text-emerald-400">{formatUSD(project.revenue)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// Units Content
const UnitsContent: React.FC = () => (
  <div className="p-4 md:p-6 space-y-4">
    <div className="flex items-center justify-between">
      <h2 className="text-base font-semibold text-zinc-900 dark:text-white">Units</h2>
      <button className="px-3 py-1.5 bg-sky-500 hover:bg-sky-600 text-white text-xs font-medium rounded-md transition-colors">
        + Add Unit
      </button>
    </div>
    <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg overflow-hidden shadow-sm">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-zinc-50 dark:bg-zinc-800/50 border-b border-zinc-200 dark:border-zinc-700">
            <th className="text-left py-2.5 px-4 font-medium text-zinc-500 dark:text-zinc-400">Unit</th>
            <th className="text-left py-2.5 px-4 font-medium text-zinc-500 dark:text-zinc-400">Project</th>
            <th className="text-left py-2.5 px-4 font-medium text-zinc-500 dark:text-zinc-400">Type</th>
            <th className="text-left py-2.5 px-4 font-medium text-zinc-500 dark:text-zinc-400">Status</th>
            <th className="text-right py-2.5 px-4 font-medium text-zinc-500 dark:text-zinc-400">Price</th>
          </tr>
        </thead>
        <tbody>
          {mockUnits.map((unit) => (
            <tr key={unit.id} className="border-b border-zinc-100 dark:border-zinc-800 last:border-0 hover:bg-zinc-50 dark:hover:bg-zinc-800/30">
              <td className="py-2.5 px-4 font-medium text-zinc-900 dark:text-white">{unit.name}</td>
              <td className="py-2.5 px-4 text-zinc-600 dark:text-zinc-400">{unit.project}</td>
              <td className="py-2.5 px-4 text-zinc-600 dark:text-zinc-400">{unit.type}</td>
              <td className="py-2.5 px-4">
                <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${
                  unit.status === 'Available' ? 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400' :
                  unit.status === 'Reserved' ? 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400' :
                  'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400'
                }`}>{unit.status}</span>
              </td>
              <td className="py-2.5 px-4 text-right font-semibold text-sky-600 dark:text-sky-400">{formatUSD(unit.price)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// Leads Content
const LeadsContent: React.FC = () => (
  <div className="p-4 md:p-6 space-y-4">
    <div className="flex items-center justify-between">
      <h2 className="text-base font-semibold text-zinc-900 dark:text-white">Leads</h2>
      <button className="px-3 py-1.5 bg-sky-500 hover:bg-sky-600 text-white text-xs font-medium rounded-md transition-colors">
        + Add Lead
      </button>
    </div>
    <div className="grid gap-3">
      {mockLeads.map((lead) => (
        <div key={lead.id} className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-gradient-to-br from-sky-400 to-sky-600 rounded-full flex items-center justify-center">
                <span className="text-white text-xs font-bold">{lead.name.split(' ').map(n => n[0]).join('')}</span>
              </div>
              <div>
                <h3 className="text-sm font-medium text-zinc-900 dark:text-white">{lead.name}</h3>
                <p className="text-xs text-zinc-500">{lead.email}</p>
              </div>
            </div>
            <span className={`px-2.5 py-1 text-[10px] font-medium rounded-full ${
              lead.stage === 'New' ? 'bg-sky-100 dark:bg-sky-500/20 text-sky-700 dark:text-sky-400' :
              lead.stage === 'Contacted' ? 'bg-violet-100 dark:bg-violet-500/20 text-violet-700 dark:text-violet-400' :
              lead.stage === 'Qualified' ? 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400' :
              'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400'
            }`}>{lead.stage}</span>
          </div>
          <div className="flex items-center gap-4 text-xs text-zinc-500">
            <span>Source: {lead.source}</span>
            <span>Interest: {lead.interest}</span>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Team Content
const TeamContent: React.FC = () => (
  <div className="p-4 md:p-6 space-y-4">
    <div className="flex items-center justify-between">
      <h2 className="text-base font-semibold text-zinc-900 dark:text-white">Team Management</h2>
      <button className="px-3 py-1.5 bg-sky-500 hover:bg-sky-600 text-white text-xs font-medium rounded-md transition-colors">
        + Invite Member
      </button>
    </div>
    <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg overflow-hidden shadow-sm">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-zinc-50 dark:bg-zinc-800/50 border-b border-zinc-200 dark:border-zinc-700">
            <th className="text-left py-2.5 px-4 font-medium text-zinc-500 dark:text-zinc-400">Member</th>
            <th className="text-left py-2.5 px-4 font-medium text-zinc-500 dark:text-zinc-400">Role</th>
            <th className="text-left py-2.5 px-4 font-medium text-zinc-500 dark:text-zinc-400">Sales</th>
            <th className="text-left py-2.5 px-4 font-medium text-zinc-500 dark:text-zinc-400">Status</th>
            <th className="text-right py-2.5 px-4 font-medium text-zinc-500 dark:text-zinc-400">Commission</th>
          </tr>
        </thead>
        <tbody>
          {mockTeam.map((member) => (
            <tr key={member.id} className="border-b border-zinc-100 dark:border-zinc-800 last:border-0 hover:bg-zinc-50 dark:hover:bg-zinc-800/30">
              <td className="py-2.5 px-4">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-[9px] font-bold">{member.name.split(' ').map(n => n[0]).join('')}</span>
                  </div>
                  <span className="font-medium text-zinc-900 dark:text-white">{member.name}</span>
                </div>
              </td>
              <td className="py-2.5 px-4 text-zinc-600 dark:text-zinc-400">{member.role}</td>
              <td className="py-2.5 px-4 text-zinc-600 dark:text-zinc-400">{member.sales}</td>
              <td className="py-2.5 px-4">
                <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400">
                  {member.status}
                </span>
              </td>
              <td className="py-2.5 px-4 text-right font-semibold text-emerald-600 dark:text-emerald-400">{formatUSD(member.commission)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// Financial Content
const FinancialContent: React.FC = () => (
  <div className="p-4 md:p-6 space-y-4">
    <div className="flex items-center justify-between">
      <h2 className="text-base font-semibold text-zinc-900 dark:text-white">Financial Overview</h2>
      <button className="px-3 py-1.5 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-700 dark:text-zinc-300 text-xs font-medium rounded-md transition-colors flex items-center gap-1.5">
        <DownloadIcon />
        Export Report
      </button>
    </div>

    {/* Financial Stats */}
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-3 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] text-zinc-500 dark:text-zinc-400">Total Revenue</span>
          <div className="flex items-center gap-0.5 text-[10px] font-medium text-emerald-600 dark:text-emerald-400">
            <ArrowUpRightIcon />
            <span>+{mockFinancials.revenue.change}%</span>
          </div>
        </div>
        <div className="text-lg font-bold text-zinc-900 dark:text-white">{formatUSD(mockFinancials.revenue.current)}</div>
        <div className="text-[10px] text-zinc-500">vs {formatUSD(mockFinancials.revenue.previous)} last year</div>
      </div>
      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-3 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] text-zinc-500 dark:text-zinc-400">Total Expenses</span>
          <div className="flex items-center gap-0.5 text-[10px] font-medium text-rose-600 dark:text-rose-400">
            <ArrowDownRightIcon />
            <span>+{mockFinancials.expenses.change}%</span>
          </div>
        </div>
        <div className="text-lg font-bold text-zinc-900 dark:text-white">{formatUSD(mockFinancials.expenses.current)}</div>
        <div className="text-[10px] text-zinc-500">vs {formatUSD(mockFinancials.expenses.previous)} last year</div>
      </div>
      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-3 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] text-zinc-500 dark:text-zinc-400">Net Profit</span>
          <div className="flex items-center gap-0.5 text-[10px] font-medium text-emerald-600 dark:text-emerald-400">
            <ArrowUpRightIcon />
            <span>+{mockFinancials.profit.change}%</span>
          </div>
        </div>
        <div className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{formatUSD(mockFinancials.profit.current)}</div>
        <div className="text-[10px] text-zinc-500">74% margin</div>
      </div>
      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-3 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] text-zinc-500 dark:text-zinc-400">Pending Payments</span>
          <span className="px-1.5 py-0.5 text-[9px] font-medium rounded bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400">3 pending</span>
        </div>
        <div className="text-lg font-bold text-amber-600 dark:text-amber-400">{formatUSD(mockFinancials.pendingPayments)}</div>
        <div className="text-[10px] text-zinc-500">Due this month</div>
      </div>
    </div>

    {/* Revenue Chart */}
    <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-4 shadow-sm">
      <h3 className="text-sm font-semibold text-zinc-900 dark:text-white mb-4">Revenue vs Expenses</h3>
      <div className="h-32 flex items-end gap-2">
        {mockFinancials.monthlyData.map((item, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-1">
            <div className="w-full flex gap-0.5 items-end justify-center h-24">
              <div
                className="w-3 bg-sky-500 rounded-t"
                style={{ height: `${(item.revenue / 5500000) * 100}%` }}
              />
              <div
                className="w-3 bg-rose-400 rounded-t"
                style={{ height: `${(item.expenses / 5500000) * 100}%` }}
              />
            </div>
            <span className="text-[9px] text-zinc-400">{item.month}</span>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-center gap-4 mt-3 text-[10px]">
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded bg-sky-500" />
          <span className="text-zinc-500">Revenue</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded bg-rose-400" />
          <span className="text-zinc-500">Expenses</span>
        </div>
      </div>
    </div>
  </div>
);

// Commissions Content
const CommissionsContent: React.FC = () => (
  <div className="p-4 md:p-6 space-y-4">
    <div className="flex items-center justify-between">
      <h2 className="text-base font-semibold text-zinc-900 dark:text-white">Commission Tracking</h2>
      <button className="px-3 py-1.5 bg-sky-500 hover:bg-sky-600 text-white text-xs font-medium rounded-md transition-colors">
        + Record Sale
      </button>
    </div>

    {/* Commission Stats */}
    <div className="grid grid-cols-3 gap-3">
      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-3 shadow-sm">
        <div className="text-[10px] text-zinc-500 dark:text-zinc-400 mb-1">Total Commissions</div>
        <div className="text-lg font-bold text-zinc-900 dark:text-white">{formatUSD(mockStats.totalCommissions)}</div>
      </div>
      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-3 shadow-sm">
        <div className="text-[10px] text-zinc-500 dark:text-zinc-400 mb-1">Pending Payout</div>
        <div className="text-lg font-bold text-amber-600 dark:text-amber-400">{formatUSD(43750)}</div>
      </div>
      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-3 shadow-sm">
        <div className="text-[10px] text-zinc-500 dark:text-zinc-400 mb-1">Avg. Rate</div>
        <div className="text-lg font-bold text-zinc-900 dark:text-white">5%</div>
      </div>
    </div>

    {/* Commissions Table */}
    <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg overflow-hidden shadow-sm">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-zinc-50 dark:bg-zinc-800/50 border-b border-zinc-200 dark:border-zinc-700">
            <th className="text-left py-2.5 px-4 font-medium text-zinc-500 dark:text-zinc-400">Agent</th>
            <th className="text-left py-2.5 px-4 font-medium text-zinc-500 dark:text-zinc-400">Unit</th>
            <th className="text-left py-2.5 px-4 font-medium text-zinc-500 dark:text-zinc-400">Sale Price</th>
            <th className="text-left py-2.5 px-4 font-medium text-zinc-500 dark:text-zinc-400">Rate</th>
            <th className="text-left py-2.5 px-4 font-medium text-zinc-500 dark:text-zinc-400">Status</th>
            <th className="text-right py-2.5 px-4 font-medium text-zinc-500 dark:text-zinc-400">Amount</th>
          </tr>
        </thead>
        <tbody>
          {mockCommissions.map((comm) => (
            <tr key={comm.id} className="border-b border-zinc-100 dark:border-zinc-800 last:border-0 hover:bg-zinc-50 dark:hover:bg-zinc-800/30">
              <td className="py-2.5 px-4 font-medium text-zinc-900 dark:text-white">{comm.agent}</td>
              <td className="py-2.5 px-4 text-zinc-600 dark:text-zinc-400">{comm.unit}</td>
              <td className="py-2.5 px-4 text-zinc-600 dark:text-zinc-400">{formatUSD(comm.salePrice)}</td>
              <td className="py-2.5 px-4 text-zinc-600 dark:text-zinc-400">{comm.rate}%</td>
              <td className="py-2.5 px-4">
                <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${
                  comm.status === 'Paid' ? 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400' :
                  comm.status === 'Pending' ? 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400' :
                  'bg-sky-100 dark:bg-sky-500/20 text-sky-700 dark:text-sky-400'
                }`}>{comm.status}</span>
              </td>
              <td className="py-2.5 px-4 text-right font-semibold text-emerald-600 dark:text-emerald-400">{formatUSD(comm.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// Investors Content
const InvestorsContent: React.FC = () => (
  <div className="p-4 md:p-6 space-y-4">
    <div className="flex items-center justify-between">
      <h2 className="text-base font-semibold text-zinc-900 dark:text-white">Investor Portal</h2>
      <button className="px-3 py-1.5 bg-sky-500 hover:bg-sky-600 text-white text-xs font-medium rounded-md transition-colors">
        + Add Investor
      </button>
    </div>

    {/* Investor Stats */}
    <div className="grid grid-cols-3 gap-3">
      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-3 shadow-sm">
        <div className="text-[10px] text-zinc-500 dark:text-zinc-400 mb-1">Total Invested</div>
        <div className="text-lg font-bold text-zinc-900 dark:text-white">{formatUSD(17550000)}</div>
      </div>
      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-3 shadow-sm">
        <div className="text-[10px] text-zinc-500 dark:text-zinc-400 mb-1">Total Returns</div>
        <div className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{formatUSD(1404000)}</div>
      </div>
      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-3 shadow-sm">
        <div className="text-[10px] text-zinc-500 dark:text-zinc-400 mb-1">Avg. ROI</div>
        <div className="text-lg font-bold text-zinc-900 dark:text-white">8%</div>
      </div>
    </div>

    {/* Investors Table */}
    <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg overflow-hidden shadow-sm">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-zinc-50 dark:bg-zinc-800/50 border-b border-zinc-200 dark:border-zinc-700">
            <th className="text-left py-2.5 px-4 font-medium text-zinc-500 dark:text-zinc-400">Investor</th>
            <th className="text-left py-2.5 px-4 font-medium text-zinc-500 dark:text-zinc-400">Type</th>
            <th className="text-left py-2.5 px-4 font-medium text-zinc-500 dark:text-zinc-400">Invested</th>
            <th className="text-left py-2.5 px-4 font-medium text-zinc-500 dark:text-zinc-400">Units</th>
            <th className="text-left py-2.5 px-4 font-medium text-zinc-500 dark:text-zinc-400">Status</th>
            <th className="text-right py-2.5 px-4 font-medium text-zinc-500 dark:text-zinc-400">Returns</th>
          </tr>
        </thead>
        <tbody>
          {mockInvestors.map((investor) => (
            <tr key={investor.id} className="border-b border-zinc-100 dark:border-zinc-800 last:border-0 hover:bg-zinc-50 dark:hover:bg-zinc-800/30">
              <td className="py-2.5 px-4 font-medium text-zinc-900 dark:text-white">{investor.name}</td>
              <td className="py-2.5 px-4">
                <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${
                  investor.type === 'Institutional' ? 'bg-violet-100 dark:bg-violet-500/20 text-violet-700 dark:text-violet-400' :
                  investor.type === 'Family Office' ? 'bg-sky-100 dark:bg-sky-500/20 text-sky-700 dark:text-sky-400' :
                  investor.type === 'VC' ? 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400' :
                  'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400'
                }`}>{investor.type}</span>
              </td>
              <td className="py-2.5 px-4 text-zinc-600 dark:text-zinc-400">{formatUSD(investor.invested)}</td>
              <td className="py-2.5 px-4 text-zinc-600 dark:text-zinc-400">{investor.units}</td>
              <td className="py-2.5 px-4">
                <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400">
                  {investor.status}
                </span>
              </td>
              <td className="py-2.5 px-4 text-right font-semibold text-emerald-600 dark:text-emerald-400">{formatUSD(investor.returns)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// Tools Content
const ToolsContent: React.FC = () => (
  <div className="p-4 md:p-6 space-y-4">
    <div className="flex items-center justify-between">
      <h2 className="text-base font-semibold text-zinc-900 dark:text-white">Sales Tools</h2>
    </div>

    {/* Tools Grid */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
      {mockSalesTools.map((tool) => (
        <div key={tool.id} className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-4 shadow-sm hover:border-sky-300 dark:hover:border-sky-700 transition-colors cursor-pointer">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-sky-100 dark:bg-sky-500/20 flex items-center justify-center text-sky-600 dark:text-sky-400">
              {tool.icon === 'calculator' && <CalculatorIcon />}
              {tool.icon === 'document' && <FileIcon />}
              {tool.icon === 'chart' && <BarChart3Icon />}
              {tool.icon === 'grid' && <GridIcon />}
              {tool.icon === 'percent' && <PercentIcon />}
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-zinc-900 dark:text-white mb-0.5">{tool.name}</h3>
              <p className="text-[11px] text-zinc-500 mb-2">{tool.description}</p>
              <div className="flex items-center gap-1 text-[10px] text-zinc-400">
                <span>{tool.usage} uses this month</span>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>

    {/* Quick Actions */}
    <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-4 shadow-sm">
      <h3 className="text-sm font-semibold text-zinc-900 dark:text-white mb-3">Quick Actions</h3>
      <div className="flex flex-wrap gap-2">
        <button className="px-3 py-1.5 bg-sky-500 hover:bg-sky-600 text-white text-xs font-medium rounded-md transition-colors">
          Generate Proposal
        </button>
        <button className="px-3 py-1.5 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-700 dark:text-zinc-300 text-xs font-medium rounded-md transition-colors">
          Export Price List
        </button>
        <button className="px-3 py-1.5 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-700 dark:text-zinc-300 text-xs font-medium rounded-md transition-colors">
          Calculate ROI
        </button>
      </div>
    </div>
  </div>
);

// Media Content
const MediaContent: React.FC = () => (
  <div className="p-4 md:p-6 space-y-4">
    <div className="flex items-center justify-between">
      <h2 className="text-base font-semibold text-zinc-900 dark:text-white">Media Library</h2>
      <button className="px-3 py-1.5 bg-sky-500 hover:bg-sky-600 text-white text-xs font-medium rounded-md transition-colors flex items-center gap-1.5">
        <UploadIcon />
        Upload Files
      </button>
    </div>

    {/* Media Stats */}
    <div className="grid grid-cols-4 gap-3">
      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-3 shadow-sm text-center">
        <div className="text-lg font-bold text-zinc-900 dark:text-white">24</div>
        <div className="text-[10px] text-zinc-500">Documents</div>
      </div>
      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-3 shadow-sm text-center">
        <div className="text-lg font-bold text-zinc-900 dark:text-white">156</div>
        <div className="text-[10px] text-zinc-500">Images</div>
      </div>
      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-3 shadow-sm text-center">
        <div className="text-lg font-bold text-zinc-900 dark:text-white">8</div>
        <div className="text-[10px] text-zinc-500">Videos</div>
      </div>
      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-3 shadow-sm text-center">
        <div className="text-lg font-bold text-zinc-900 dark:text-white">2.4 GB</div>
        <div className="text-[10px] text-zinc-500">Total Size</div>
      </div>
    </div>

    {/* Media Table */}
    <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg overflow-hidden shadow-sm">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-zinc-50 dark:bg-zinc-800/50 border-b border-zinc-200 dark:border-zinc-700">
            <th className="text-left py-2.5 px-4 font-medium text-zinc-500 dark:text-zinc-400">File Name</th>
            <th className="text-left py-2.5 px-4 font-medium text-zinc-500 dark:text-zinc-400">Type</th>
            <th className="text-left py-2.5 px-4 font-medium text-zinc-500 dark:text-zinc-400">Project</th>
            <th className="text-left py-2.5 px-4 font-medium text-zinc-500 dark:text-zinc-400">Size</th>
            <th className="text-right py-2.5 px-4 font-medium text-zinc-500 dark:text-zinc-400">Actions</th>
          </tr>
        </thead>
        <tbody>
          {mockMedia.map((file) => (
            <tr key={file.id} className="border-b border-zinc-100 dark:border-zinc-800 last:border-0 hover:bg-zinc-50 dark:hover:bg-zinc-800/30">
              <td className="py-2.5 px-4">
                <div className="flex items-center gap-2">
                  <div className={`w-7 h-7 rounded flex items-center justify-center ${
                    file.type === 'PDF' ? 'bg-rose-100 dark:bg-rose-500/20 text-rose-600 dark:text-rose-400' :
                    file.type === 'Images' ? 'bg-sky-100 dark:bg-sky-500/20 text-sky-600 dark:text-sky-400' :
                    file.type === 'Video' ? 'bg-violet-100 dark:bg-violet-500/20 text-violet-600 dark:text-violet-400' :
                    'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400'
                  }`}>
                    {file.type === 'PDF' && <FileIcon />}
                    {file.type === 'Images' && <ImageIcon />}
                    {file.type === 'Video' && <VideoIcon />}
                    {file.type === 'Spreadsheet' && <FileIcon />}
                  </div>
                  <span className="font-medium text-zinc-900 dark:text-white truncate max-w-[150px]">{file.name}</span>
                </div>
              </td>
              <td className="py-2.5 px-4 text-zinc-600 dark:text-zinc-400">{file.type}</td>
              <td className="py-2.5 px-4 text-zinc-600 dark:text-zinc-400">{file.project}</td>
              <td className="py-2.5 px-4 text-zinc-600 dark:text-zinc-400">{file.size}</td>
              <td className="py-2.5 px-4 text-right">
                <button className="p-1 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded text-zinc-500">
                  <DownloadIcon />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// Settings Content
const SettingsContent: React.FC = () => {
  const [darkMode, setDarkMode] = useState(false);

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-zinc-900 dark:text-white">Settings</h2>
      </div>

      {/* Settings Sections */}
      <div className="space-y-4">
        {/* Workspace Settings */}
        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-white mb-3">Workspace</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between py-2 border-b border-zinc-100 dark:border-zinc-800">
              <div>
                <p className="text-xs font-medium text-zinc-900 dark:text-white">Company Name</p>
                <p className="text-[10px] text-zinc-500">PropertyBase Demo</p>
              </div>
              <button className="text-[10px] text-sky-600 dark:text-sky-400 font-medium">Edit</button>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-zinc-100 dark:border-zinc-800">
              <div>
                <p className="text-xs font-medium text-zinc-900 dark:text-white">Default Currency</p>
                <p className="text-[10px] text-zinc-500">USD ($)</p>
              </div>
              <button className="text-[10px] text-sky-600 dark:text-sky-400 font-medium">Change</button>
            </div>
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="text-xs font-medium text-zinc-900 dark:text-white">Timezone</p>
                <p className="text-[10px] text-zinc-500">Asia/Bali (GMT+8)</p>
              </div>
              <button className="text-[10px] text-sky-600 dark:text-sky-400 font-medium">Change</button>
            </div>
          </div>
        </div>

        {/* Appearance */}
        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-white mb-3">Appearance</h3>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-zinc-900 dark:text-white">Dark Mode</p>
              <p className="text-[10px] text-zinc-500">Toggle dark theme</p>
            </div>
            <button
              onClick={() => setDarkMode(!darkMode)}
              className={`w-9 h-5 rounded-full transition-colors ${darkMode ? 'bg-sky-500' : 'bg-zinc-300 dark:bg-zinc-600'}`}
            >
              <div className={`w-4 h-4 rounded-full bg-white shadow transition-transform ${darkMode ? 'translate-x-4' : 'translate-x-0.5'}`} />
            </button>
          </div>
        </div>

        {/* Notifications */}
        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-white mb-3">Notifications</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-zinc-900 dark:text-white">Email Notifications</p>
                <p className="text-[10px] text-zinc-500">Receive updates via email</p>
              </div>
              <div className="w-9 h-5 rounded-full bg-sky-500">
                <div className="w-4 h-4 rounded-full bg-white shadow translate-x-4" />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-zinc-900 dark:text-white">Lead Alerts</p>
                <p className="text-[10px] text-zinc-500">Get notified for new leads</p>
              </div>
              <div className="w-9 h-5 rounded-full bg-sky-500">
                <div className="w-4 h-4 rounded-full bg-white shadow translate-x-4" />
              </div>
            </div>
          </div>
        </div>

        {/* Danger Zone */}
        <div className="bg-white dark:bg-zinc-900 border border-rose-200 dark:border-rose-800 rounded-lg p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-rose-600 dark:text-rose-400 mb-3">Danger Zone</h3>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-zinc-900 dark:text-white">Delete Workspace</p>
              <p className="text-[10px] text-zinc-500">Permanently delete all data</p>
            </div>
            <button className="px-3 py-1.5 bg-rose-100 dark:bg-rose-500/20 hover:bg-rose-200 dark:hover:bg-rose-500/30 text-rose-700 dark:text-rose-400 text-xs font-medium rounded-md transition-colors">
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// Main Demo Component
// ============================================
interface DemoContentProps {
  activePage: PageType;
  onToggle: () => void;
}

const DemoContent: React.FC<DemoContentProps> = ({ activePage, onToggle }) => {
  return (
    <div className="flex-1 overflow-hidden flex flex-col bg-zinc-50 dark:bg-zinc-950">
      {/* Header - matching actual app */}
      <header className="sticky top-0 z-10 flex h-12 items-center gap-2 border-b border-zinc-200 dark:border-zinc-800 px-4 bg-white/95 dark:bg-zinc-950/95 backdrop-blur">
        <button
          onClick={onToggle}
          className="p-1.5 rounded-md hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-500 dark:text-zinc-400"
        >
          <PanelLeftIcon />
        </button>
        <div className="flex-1" />
        <div className="flex items-center gap-1">
          <button className="p-1.5 rounded-md hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-500 dark:text-zinc-400">
            <SearchIcon />
          </button>
          <button className="p-1.5 rounded-md hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-500 dark:text-zinc-400 relative">
            <BellIcon />
            <span className="absolute top-1 right-1 w-1.5 h-1.5 bg-sky-500 rounded-full" />
          </button>
          <div className="w-px h-6 bg-zinc-200 dark:bg-zinc-700 mx-1" />
          <div className="w-7 h-7 bg-gradient-to-br from-violet-500 to-violet-600 rounded-full flex items-center justify-center">
            <span className="text-white font-medium text-[9px]">AD</span>
          </div>
        </div>
      </header>

      {/* Page Content */}
      <div className="flex-1 overflow-y-auto">
        {activePage === 'overview' && <OverviewContent />}
        {activePage === 'projects' && <ProjectsContent />}
        {activePage === 'units' && <UnitsContent />}
        {activePage === 'team' && <TeamContent />}
        {activePage === 'leads' && <LeadsContent />}
        {activePage === 'financial' && <FinancialContent />}
        {activePage === 'commissions' && <CommissionsContent />}
        {activePage === 'investors' && <InvestorsContent />}
        {activePage === 'tools' && <ToolsContent />}
        {activePage === 'media' && <MediaContent />}
        {activePage === 'settings' && <SettingsContent />}
      </div>
    </div>
  );
};

// ============================================
// Interactive Demo Wrapper
// ============================================
export const PropertyBaseAdminDemo: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [activePage, setActivePage] = useState<PageType>('overview');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  return (
    <div className="relative">
      {/* Demo Badge */}
      <div className="absolute top-3 right-3 z-20 flex items-center gap-2">
        <span className="px-2.5 py-1 bg-sky-500/20 border border-sky-500/30 text-sky-400 text-xs font-medium rounded-full backdrop-blur-sm">
          Live Demo
        </span>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="px-2.5 py-1 bg-zinc-800/80 border border-zinc-700 text-zinc-300 text-xs font-medium rounded-full backdrop-blur-sm hover:bg-zinc-700/80 transition-colors"
        >
          {isExpanded ? 'Collapse' : 'Expand'}
        </button>
      </div>

      {/* Dashboard Preview */}
      <motion.div
        className="relative rounded-xl overflow-hidden border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 shadow-2xl"
        animate={{ height: isExpanded ? 600 : 400 }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex h-full">
          {/* Sidebar */}
          <DemoSidebar
            activePage={activePage}
            onPageChange={setActivePage}
            collapsed={sidebarCollapsed}
          />

          {/* Main Content */}
          <DemoContent
            activePage={activePage}
            onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
          />
        </div>

        {/* Demo Overlay Gradient */}
        {!isExpanded && (
          <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white dark:from-zinc-950 to-transparent pointer-events-none" />
        )}
      </motion.div>

      {/* Info Bar */}
      <div className="mt-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-sky-500 animate-pulse" />
            <span className="text-xs text-zinc-400">Interactive Demo</span>
          </div>
          <span className="text-xs text-zinc-600">|</span>
          <span className="text-xs text-zinc-500">Click sidebar tabs to explore • No database connection</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-zinc-500">
          <span>Built with</span>
          <span className="px-2 py-0.5 bg-zinc-800 rounded text-zinc-400">React</span>
          <span className="px-2 py-0.5 bg-zinc-800 rounded text-zinc-400">Vite</span>
          <span className="px-2 py-0.5 bg-zinc-800 rounded text-zinc-400">shadcn/ui</span>
        </div>
      </div>
    </div>
  );
};

export default PropertyBaseAdminDemo;
